import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError } from '../notify';

// Karma farming: each account's daily comment plans (Generate-Week writes
// them — backend/helpers/karmaFarming.js) and its comment queue
// (backend/helpers/karmaQueue.js). The account works on its own: the browser
// finds a fresh question shortly before each turn, a reply is drafted and
// approved automatically and posted when the turn comes. This panel shows
// each step live; a person can edit (holds the reply) or cancel until the
// browser takes it. Any error from Reddit stops the account until a person
// presses Resume. Shows nothing when the accounts on screen have no plans.
// An account that runs on a phone works only after Start replying (today's
// plan only): the server finds the questions and writes the replies, and each
// reply goes to the phone fleet as one short job (backend karmaQueue fleetTick).
const dayLabel = (d) => new Date(d + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const today = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
const small = { fontSize: '11px', padding: '2px 8px' };
const HOLD_MIN = 10; // backend karmaQueue HOLD_MIN
const clock = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const title = (item) => (
  <a href={item.thread_url} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: '#fff', fontWeight: 600, textDecoration: 'none' }}>{item.thread_title}</a>
);
const line = (color, text) => <div style={{ fontSize: '12px', color, marginTop: '4px' }}>{text}</div>;

// The reply the system picked, with its countdown, Edit and Cancel
function Reply({ item, timing, now, onChanged, phone }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.approved_text || '');
  const [busy, setBusy] = useState(false);
  const call = async (action, body, fail) => {
    setBusy(true);
    try { await axios.post(`${API}/api/karma/queue/${item.id}/${action}`, body || {}); setBusy(false); onChanged(); return true; }
    catch (e) { notifyError(e, fail); setBusy(false); onChanged(); return false; }
  };
  const edit = async () => { setText(item.approved_text || ''); if (await call('hold', null, 'Could not hold the reply')) setEditing(true); };
  const save = async () => { if (await call('release', { text }, 'Could not save')) setEditing(false); };
  const back = async () => { if (await call('release', null, 'Could not release the reply')) setEditing(false); };

  const held = item.status === 'editing';
  const postsIn = (timing?.next_at ? Date.parse(timing.next_at) : 0) - now;
  return (
    <div>
      {title(item)}
      {editing ? (
        <>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
            style={{ width: '100%', fontSize: '13px', padding: '6px 8px', background: '#141414', color: '#ddd', border: '1px solid #333', borderRadius: '6px', boxSizing: 'border-box', margin: '6px 0' }} />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button style={small} disabled={busy || !text.trim()} onClick={save}>Save</button>
            <button className="btn-secondary" style={small} disabled={busy} onClick={back}>Back</button>
            <span style={{ fontSize: '11px', color: '#888' }}>Held while you edit (up to {HOLD_MIN} min)</span>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: '12px', color: '#ccc', margin: '6px 0', padding: '6px 8px', background: '#1a1a1a', borderRadius: '6px', whiteSpace: 'pre-wrap' }}>{item.approved_text}</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', color: '#f59e0b', minWidth: '120px' }}>
              {held ? `✎ Held for editing · back in the queue in ${clock(Date.parse(item.claimed_at) + HOLD_MIN * 60000 - now)}`
                : postsIn > 0 ? `⏳ ${phone ? 'Goes to the phone' : 'Posting'} in ${clock(postsIn)}` : phone ? '⏳ Sending it to the phone fleet…' : '⏳ Posting at the next check-in…'}
            </span>
            <button className="btn-secondary" style={small} disabled={busy} onClick={edit}>Edit reply</button>
            {held && <button className="btn-secondary" style={small} disabled={busy} onClick={back}>Release</button>}
            <button className="btn-secondary" style={{ ...small, color: '#ef4444' }} disabled={busy} onClick={() => call('cancel', null, 'Could not cancel')}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

// An account Reddit's error stopped: the error, and Resume
function Stopped({ halt, onChanged }) {
  const [busy, setBusy] = useState(false);
  const resume = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/karma/accounts/${halt.account_id}/resume`); }
    catch (e) { notifyError(e, 'Could not resume'); }
    setBusy(false);
    onChanged();
  };
  // a phone that couldn't take the reply (fleet) vs Reddit refusing the account
  const phoneStop = /^the phone: /.test(halt.error || '');
  return (
    <>
      {line('#ef4444', phoneStop ? `⛔ Stopped — phone problem: ${halt.error.replace(/^the phone: /, '')}` : `⛔ Stopped — Reddit said: ${halt.error || 'an error'} (${halt.subreddit})`)}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', color: '#888' }}>{phoneStop ? 'Send this to the fleet manager. Press Resume (or Start replying) once they have fixed it.' : 'Nothing is looked for or posted for this account until you resume it.'}</span>
        <button className="btn-secondary" style={small} disabled={busy} onClick={resume}>Resume</button>
      </div>
    </>
  );
}

// What one account is doing right now
function Live({ username, item, halt, timing, now, onChanged, phone }) {
  let body;
  if (halt) body = <Stopped halt={halt} onChanged={onChanged} />;
  else if (phone?.job && item) body = <>{title(item)}{phoneJobLine(phone.job)}</>;
  else if (item && (item.status === 'approved' || item.status === 'editing')) body = <Reply key={item.id} item={item} timing={timing} now={now} onChanged={onChanged} phone={!!phone} />;
  else if (item?.status === 'waiting') body = <>{title(item)}{line('#0ea5e9', '✍ Writing the reply…')}</>;
  else if (item?.status === 'posting') body = <>{title(item)}{line('#f59e0b', phone ? '📱 The phone is posting it now' : '📤 The browser is posting it now')}</>;
  else if (timing?.find_at && Date.parse(timing.find_at) > now) body = line('#888', `⏸ Next comment in ${clock(Date.parse(timing.next_at) - now)} (Reddit's pause between comments)`);
  else body = line('#0ea5e9', phone ? '🔎 Looking for a fresh question… (the scraper reads the subs)' : '🔎 Looking for a fresh question… (needs the browser open)');
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid #1f1f1f' }}>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {phone ? '📱 ' : ''}u/{username}{item && !halt ? ` · ${item.subreddit} · ▲${item.thread_score ?? '?'} · ${item.thread_comments ?? '?'} comments` : ''}
      </div>
      {body}
    </div>
  );
}

const STATUS_LINE = {
  posted: ['#22c55e', '✓ Posted'],
  failed: ['#ef4444', '⚠ Not posted'],
  halted: ['#ef4444', '⛔ Stopped the account'],
  skipped: ['#888', '✕ Cancelled'],
  expired: ['#888', '⌛ Timed out']
};
const OPEN = ['waiting', 'approved', 'editing', 'posting'];
// A posted reply: how old the thread was when it went up, and what it got
const outcomeLine = (item) => {
  const at = item.thread_created_at && item.posted_at ? Math.round((Date.parse(item.posted_at) - Date.parse(item.thread_created_at)) / 60000) : null;
  const o = item.outcome;
  return [at != null ? `posted at ${at} min` : null,
    o ? `▲${o.score}` : 'score after 1 hour',
    o && o.thread_comments != null ? `thread ${o.thread_comments} comments` : null].filter(Boolean).join(' · ');
};
const hhmm = (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// A phone account's reply job, as the fleet reports it
function phoneJobLine(job) {
  if (job.status === 'running') return line('#22c55e', job.cancel_requested ? '■ Stopping on the phone…' : '📱 The phone is posting it now');
  if (job.fleet_state === 'waiting') return line('#f59e0b', `⏳ Waiting at the fleet${job.position ? ` · #${job.position} in line` : ''}${job.reason ? ` · ${job.reason}` : ''}${job.start_by ? ` · must start by ${hhmm(job.start_by)}` : ''}`);
  return line('#f59e0b', `📨 Sent to the fleet${job.start_by ? ` · must start by ${hhmm(job.start_by)}` : ''}`);
}

export default function CommentPlans({ accounts }) {
  const [plans, setPlans] = useState([]);
  const [queue, setQueue] = useState([]);
  const [timings, setTimings] = useState({});
  // Accounts whose browser posts the replies: only those count, so no
  // counters for comments posted by hand (a stray +1 ended the day early)
  const [automatic, setAutomatic] = useState([]);
  // phone accounts: { [account id]: { replying, replying_by, job } }
  const [fleet, setFleet] = useState({});
  const [busy, setBusy] = useState({});
  const [now, setNow] = useState(Date.now());
  const ids = (accounts || []).map(a => a.id).join(',');
  // Which account list the data on screen belongs to: after a switch nothing
  // shows until the new list's data is in, and a late answer for an older
  // list is dropped
  const [loadedFor, setLoadedFor] = useState(null);
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const load = useCallback(async () => {
    if (!ids) { setPlans([]); setQueue([]); setTimings({}); setLoadedFor(ids); return; }
    try {
      const [p, q] = await Promise.all([
        axios.get(`${API}/api/karma/plans`, { params: { account_ids: ids } }),
        axios.get(`${API}/api/karma/queue`, { params: { account_ids: ids } })
      ]);
      if (idsRef.current !== ids) return;
      setLoadedFor(ids);
      setPlans(p.data?.plans || []);
      setAutomatic(p.data?.automatic || []);
      setFleet(p.data?.fleet || {});
      setQueue(q.data?.items || []);
      setTimings(q.data?.timings || {});
    } catch (e) { /* not shown: the Scheduler works without it */ }
  }, [ids]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(t);
  }, [load]);

  const t = today();
  const shown = plans.filter(p => p.plan_date >= t || p.status === 'planned');
  // plans are dated in UTC (backend todayUtc)
  const utcToday = new Date().toISOString().slice(0, 10);
  const working = shown.filter(p => p.plan_date === utcToday && p.status === 'planned' && p.done < p.planned
    && (fleet[p.account_id] ? fleet[p.account_id].replying : automatic.includes(p.account_id)));
  useEffect(() => {
    if (!working.length) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [working.length]);

  // Start / Stop replying: a phone account's plan for today
  const replying = async (accountId, on) => {
    setBusy(b => ({ ...b, [accountId]: true }));
    try { await axios.post(`${API}/api/karma/accounts/${accountId}/replying`, { on }); await load(); }
    catch (e) { notifyError(e, on ? 'Could not start replying' : 'Could not stop replying'); }
    setBusy(b => ({ ...b, [accountId]: false }));
  };

  const progress = async (plan, body) => {
    setBusy(b => ({ ...b, [plan.id]: true }));
    try {
      const res = await axios.post(`${API}/api/karma/plans/${plan.id}/progress`, body);
      setPlans(ps => ps.map(p => (p.id === plan.id ? res.data.plan : p)));
    } catch (e) { notifyError(e, 'Could not save'); }
    setBusy(b => ({ ...b, [plan.id]: false }));
  };

  if (loadedFor !== ids || (!shown.length && !queue.length)) return null;
  const nameOf = Object.fromEntries((accounts || []).map(a => [a.id, a.reddit_username]));
  const openOf = (accountId) => queue.find(q => q.account_id === accountId && OPEN.includes(q.status));
  const haltOf = (accountId) => queue.find(q => q.account_id === accountId && q.status === 'halted');
  // stopped accounts stay on screen whatever their plans say
  const live = [...new Set([...working.map(p => p.account_id), ...queue.filter(q => q.status === 'halted').map(q => q.account_id)])];
  const results = queue.filter(q => STATUS_LINE[q.status]);

  return (
    <div className="card" style={{ borderColor: '#0e7490' }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '4px' }}>💬 Karma farming — comments</div>
      <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>
        Runs on its own while the account's browser is open (📱 phone accounts: after <b style={{ color: '#bbb' }}>Start replying</b>, today only — each reply goes to the phone fleet as a short job): shortly before each turn it finds a fresh question, writes a reply and posts it (no links, no promotion). Watch it here — <b style={{ color: '#bbb' }}>Edit reply</b> holds a reply until you save it, <b style={{ color: '#bbb' }}>Cancel</b> drops it. Comments go out 1–9 minutes apart (the more comment karma, the shorter the wait). <b style={{ color: '#bbb' }}>Any error from Reddit stops the account</b> until you press Resume.
      </div>
      {shown.map(p => {
        const past = p.plan_date < t;
        const ph = fleet[p.account_id];
        const auto = !!ph || automatic.includes(p.account_id);
        const canReply = ph && p.plan_date === utcToday && p.status === 'planned' && p.done < p.planned;
        return (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid #1f1f1f', opacity: p.status === 'planned' ? 1 : 0.6 }}>
            <span style={{ fontSize: '12px', color: past ? '#f59e0b' : p.plan_date === t ? '#22c55e' : '#aaa', minWidth: '96px' }}>{dayLabel(p.plan_date)}{p.plan_date === t ? ' · today' : ''}</span>
            <span style={{ fontSize: '12px', color: '#ddd', minWidth: '120px' }}>u/{nameOf[p.account_id] || '?'}</span>
            <span style={{ fontSize: '12px', color: '#bbb', flex: 1 }}>{(p.subs || []).map(x => `${x.name} ×${x.count}`).join(' · ')}</span>
            <span style={{ fontSize: '12px', color: p.done >= p.planned ? '#22c55e' : '#aaa' }} title={auto ? 'Counted as the browser posts the replies' : undefined}>{p.status === 'skipped' ? 'skipped' : `${p.done}/${p.planned}`}</span>
            {p.status !== 'skipped' && !auto && (
              <>
                <button className="btn-secondary" style={small} disabled={busy[p.id] || p.done <= 0} onClick={() => progress(p, { done: p.done - 1 })} title="Count a comment posted by hand">−</button>
                <button className="btn-secondary" style={small} disabled={busy[p.id] || p.done >= p.planned} onClick={() => progress(p, { done: p.done + 1 })} title="Count a comment posted by hand">+1</button>
              </>
            )}
            {canReply && ph.problem && !ph.replying && <span style={{ fontSize: '11px', color: '#ef4444' }}>📱 {ph.problem}</span>}
            {canReply && ph.warning && <span style={{ fontSize: '11px', color: '#f59e0b' }}>📱 {ph.warning}</span>}
            {canReply && !(ph.problem && !ph.replying) && (ph.replying
              ? <button className="btn-secondary" style={{ ...small, color: '#ef4444' }} disabled={busy[p.account_id]} title={ph.replying_by ? `Started by ${ph.replying_by}` : undefined} onClick={() => replying(p.account_id, false)}>■ Stop replying</button>
              : <button className="btn-secondary" style={{ ...small, color: '#22c55e', borderColor: '#166534' }} disabled={busy[p.account_id]} title="Today's comments only: each reply goes to the phone fleet when its turn comes" onClick={() => replying(p.account_id, true)}>📱 Start replying</button>)}
            {p.status === 'planned' && <button className="btn-secondary" style={small} disabled={busy[p.id]} onClick={() => progress(p, { done: p.done, status: 'skipped' })}>Skip day</button>}
          </div>
        );
      })}

      {live.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '12px', color: '#0ea5e9', fontWeight: 600 }}>Live</div>
          {live.map(id => (
            <Live key={id} username={nameOf[id] || '?'} item={openOf(id)} halt={haltOf(id)}
              timing={timings[id]} now={now} onChanged={load} phone={fleet[id] || null} />
          ))}
        </div>
      )}
      {results.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '12px', color: '#888', fontWeight: 600 }}>Replies (last 24 hours)</div>
          {results.map(item => {
            // a phone's reply posted without an id: the scraper looks for it
            const [color, label] = item.status === 'posted' && item.visibility === 'resolving' ? ['#22c55e', '✓ Posted · finding the comment…']
              : item.status === 'posted' && item.visibility === 'not_visible' ? ['#f59e0b', '⚠ Posted, not publicly visible (filtered or shadow-removed?)']
              : STATUS_LINE[item.status];
            return (
              <div key={item.id} style={{ fontSize: '12px', padding: '4px 0', borderTop: '1px solid #1f1f1f', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color }}>{label}{['failed', 'halted'].includes(item.status) && item.error ? `: ${item.error}` : ''}</span>
                <span style={{ color: '#888' }}>u/{nameOf[item.account_id] || '?'} · {item.subreddit}</span>
                <a href={item.comment_url || item.thread_url} target="_blank" rel="noreferrer" style={{ color: '#bbb' }}>{item.thread_title}</a>
                {item.status === 'posted' && <span style={{ color: '#888' }} title="Our score, and how big the thread grew, at the last check (1 hour after posting, then every 6 hours)">{outcomeLine(item)}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
